import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { Input, Button, Spin } from "antd";
import { CloseOutlined, SendOutlined, ZoomOutOutlined, ZoomInOutlined } from "@ant-design/icons";
import "./chatWidget.css";
import { BASE_END_POINT } from "../../urls";

function cleanReply(text) {
  if (!text) return "";

  const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(text);
  if (looksLikeHtml) {
    const isArabic = /[\u0600-\u06FF]/.test(text);
    return `<div dir="${isArabic ? "rtl" : "ltr"}" style="text-align:${isArabic ? "right" : "left"
      }; line-height:1.6; font-size:14px; color:#333;">${text}</div>`;
  }

  let html = text;
  html = html.replace(/\*\*(.*?)\*\*/g, "$1");
  html = html.replace(/\*(.*?)\*/g, "$1");
  html = html.replace(/^\s*\d+\.\s+(.*)$/gm, "<li>$1</li>");
  if (html.includes("<li>")) {
    html =
      "<ol style='margin:4px 0; padding-right:18px; list-style-position: inside;'>" +
      html +
      "</ol>";
  }
  html = html.replace(/^\s*[-•]\s+(.*)$/gm, "<li>$1</li>");
  if (html.includes("<li>") && !html.includes("<ol>")) {
    html =
      "<ul style='margin:4px 0; padding-right:18px; list-style-position: inside;'>" +
      html +
      "</ul>";
  }

  html = html
    .split(/\n+/)
    .map((line) => {
      if (line.trim().startsWith("<li")) return line;
      if (line.trim() === "") return "";
      return `<p style="margin:4px 0;">${line.trim()}</p>`;
    })
    .join("");

  const isArabic = /[\u0600-\u06FF]/.test(text);
  return `<div dir="${isArabic ? "rtl" : "ltr"}" style="text-align:${isArabic ? "right" : "left"
    }; line-height:1.6; font-size:14px; color:#333;">${html}</div>`;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, role: "assistant", text: "👋 هلا! كيف أقدر أساعدك؟", isHtml: false },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const [zoomed, setZoomed] = useState(false);

  const messagesEndRef = useRef(null);
  const openRef = useRef(open); // ref لحالة open
  const notificationSound = useRef(
    new Audio(require("../../assets/sounds/notif.mp3"))
  );

  // تحديث قيمة الـ ref كل ما الـ open يتغير
  useEffect(() => {
    openRef.current = open;
    if (open) {
      setUnread(0); // ✅ نصفر عند فتح البوكس
      scrollToBottom();
    }
  }, [open]);

  // تطبيق الـ zoom على الصفحة مع أنيميشن سلس
  useEffect(() => {
    const root = document.documentElement;
    const rootDiv = document.getElementById('root');
    const kanbanColumn = document.querySelector('.kanban-column');
    if (!rootDiv) return;

    if (zoomed) {
      // 1. نطبق zoom فوراً + transform عكسي عشان الشكل ما يتغيرش
      rootDiv.style.transformOrigin = 'top center';
      rootDiv.style.transform = 'scale(1.4286)'; // 1/0.7
      root.style.zoom = '0.5';
      root.style.minHeight = '143vh'; // 1/0.7
      document.body.style.minHeight = '143vh';

      if (kanbanColumn) {
        kanbanColumn.style.setProperty('min-height', '1450px', 'important');
        kanbanColumn.style.setProperty('height', 'calc(600vh - 210px)', 'important');
      }



      // 2. بعد frame واحد نحط transition ونشيل الـ transform → أنيميشن سلسة
      requestAnimationFrame(() => {
        rootDiv.style.transition = 'transform 0.3s ease';
        rootDiv.style.transform = 'scale(1)';
      });

      // 3. بعد انتهاء الأنيميشن ننضف
      const cleanup = () => {
        rootDiv.style.transition = '';
        rootDiv.style.transform = '';
        rootDiv.style.transformOrigin = '';
      };
      rootDiv.addEventListener('transitionend', cleanup, { once: true });
    } else {
      // زوم إن: العكس
      rootDiv.style.transformOrigin = 'top center';
      rootDiv.style.transform = 'scale(0.7)'; // 0.7/1
      root.style.zoom = '1';
      root.style.minHeight = '';
      document.body.style.minHeight = '';
      if (kanbanColumn) {
        kanbanColumn.style.setProperty('min-height', '450px', 'important');
        kanbanColumn.style.setProperty('height', 'calc(100vh - 210px)', 'important');
      }



      requestAnimationFrame(() => {
        rootDiv.style.transition = 'transform 0.3s ease';
        rootDiv.style.transform = 'scale(1)';
      });

      const cleanup = () => {
        rootDiv.style.transition = '';
        rootDiv.style.transform = '';
        rootDiv.style.transformOrigin = '';
      };
      rootDiv.addEventListener('transitionend', cleanup, { once: true });
    }

    return () => {
      root.style.zoom = '1';
      root.style.minHeight = '';
      document.body.style.minHeight = '';
      rootDiv.style.transition = '';
      rootDiv.style.transform = '';
      rootDiv.style.transformOrigin = '';
    };
  }, [zoomed]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const toggleChat = () => {
    if (open) {
      setClosing(true);
      setTimeout(() => {
        setOpen(false);
        setClosing(false);
      }, 300);
    } else {
      setOpen(true);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMsg = {
      id: Date.now(),
      role: "user",
      text: input.trim(),
      isHtml: false,
    };
    const loadingMsg = {
      id: Date.now() + 1,
      role: "assistant",
      text: "⏳ جارى الرد...",
      isHtml: false,
    };

    setMessages((prev) => [...prev, userMsg, loadingMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch(`${BASE_END_POINT}/chatBot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: userMsg.text }),
      });

      const data = await res.json();

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === loadingMsg.id
            ? {
              ...msg,
              text: cleanReply(data.reply || "عفوا لا يمكننى الرد"),
              isHtml: true,
            }
            : msg
        )
      );

      // 🔊 تشغيل الصوت عند وصول الرد
      notificationSound.current.play().catch(() => { });

      // ✅ زيادة عداد الرسائل فقط لو البوكس مقفول فعلاً
      if (!openRef.current) {
        setUnread((u) => u + 1);
      }
    } catch (err) {
      console.error(err);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === loadingMsg.id
            ? { ...msg, text: "🚨 خطأ في السيرفر", isHtml: false }
            : msg
        )
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* زر Zoom عائم - يترندر برة #root عشان مايتأثرش بالـ transform */}
      {ReactDOM.createPortal(
        <div
          className="mobile-zoom-btn"
          onClick={() => setZoomed(prev => !prev)}
          title={zoomed ? "تكبير الصفحة" : "تصغير الصفحة"}
        >
          {zoomed ? (
            <ZoomInOutlined style={{ fontSize: '22px', color: '#1D9598' }} />
          ) : (
            <ZoomOutOutlined style={{ fontSize: '22px', color: '#1D9598' }} />
          )}
        </div>,
        document.body
      )}

      {/* زر عائم مع Badge كاستم - مخفي على الموبايل */}
      <div className="chat-float-btn" onClick={toggleChat}>
        {unread > 0 && (
          <span className="chat-badge">{unread > 9 ? "9+" : unread}</span>
        )}

        <div className={`icon-wrapper ${open ? "icon-hidden" : "icon-visible"}`}>
          {open ? (
            <CloseOutlined
              style={{
                fontSize: "28px",
                color: "#fff",
                background: "linear-gradient(135deg, #FF3547, #B71C1C)",
                padding: "20px",
                border: "1px solid #fff",
                borderRadius: "50%",
              }}
            />
          ) : (
            <img
              src={require("../../assets/images/chatbot.png")}
              alt="AI Icon"
              className="chat-icon-img"
            />
          )}
        </div>
      </div>

      {/* صندوق المحادثة */}
      {(open || closing) && (
        <div className={`chat-box ${closing ? "closing" : "opening"}`}>
          <div className="chat-header">
            <span>🤖 ساعي المساعد الذكي</span>
            <Button
              size="small"
              type="text"
              icon={<CloseOutlined style={{ color: "white" }} />}
              onClick={toggleChat}
            />
          </div>

          <div className="chat-body">
            {messages.map((m) => (
              <div key={m.id} className={`chat-message ${m.role}`}>
                {m.isHtml ? (
                  <div dangerouslySetInnerHTML={{ __html: m.text }} />
                ) : (
                  m.text
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={sendMessage} className="chat-input-area">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="chat-input"
              placeholder="أدخل البيانات..." />
            <Button
              type="primary"
              htmlType="submit"
              className="send-btn"
              icon={loading ? <Spin style={{ color: "#fff" }} /> : <SendOutlined />}
            />
          </form>
        </div>
      )}
    </div>
  );
}
